import { app } from 'src/configuration'
import styled, { css } from 'styled-components'

import { mq, sizes } from '../../theme'
import Icon from '../Icon'

const publicPathPrefix = app.ASSET_PREFIX

type ContainerProps = {
  backgroundColor?: string
  backgroundImageMobile?: string
  backgroundImageDesktop?: string
  hasBackLink?: boolean
}

export const CoverSection = styled.header<ContainerProps>`
  position: relative;
  padding-top: 6rem;
  padding-bottom: 7rem;
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor ?? theme.colors.redMandy};
  @media ${mq(sizes.tablet)} {
    padding-bottom: 10.5rem;
    margin-top: 0;
    padding-top: 3.2rem;
  }
  @media ${mq(sizes.desktop)} {
    margin-top: 0;
    padding-top: 9.4rem;
    padding-bottom: 9.6rem;
    min-height: 38rem;
  }
  @media print {
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
  }
`

export const Wrapper = styled.div<{ hasBottomContent?: boolean }>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  padding-left: 2rem;
  padding-right: 2rem;
  max-width: 128rem;
  margin-left: auto;
  margin-right: auto;
  @media ${mq(sizes.desktop)} {
    padding-top: 0;
  }
  ${({ hasBottomContent }) =>
    hasBottomContent &&
    css`
      min-height: 21rem;
      @media ${mq(sizes.tablet)} {
        min-height: 0;
      }
    `}

  @media print {
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`

export const StickyTarget = styled.div`
  position: absolute;
  background-color: red;
  width: 10rem;
  top: 0;
  height: 2px;
  opacity: 0;
  @media ${mq(sizes.desktop)} {
    height: 0;
    top: 0;
  }
`

export const StickyPlaceHolder = styled.div`
  height: 4rem;
  width: 100%;
  @media print {
    display: none;
  }
`

export const BackLinkZone = styled.div<{ isPageScrolled: boolean }>`
  position: absolute;
  z-index: ${({ theme }) => theme.zIndexes.header - 10};

  ${({ isPageScrolled }) =>
    isPageScrolled &&
    css`
      width: auto;
      position: fixed;

      @media ${mq(sizes.tablet)} {
        margin-top: -0.9rem;
      }

      @media ${mq(sizes.desktop)} {
        margin-top: 14.3rem;
        top: 0;
      }
    `}
`

export const BreadcrumbZone = styled.div<{
  hasBackLink?: boolean
  $showInMobile?: boolean
}>`
  display: ${({ $showInMobile }) => ($showInMobile ? 'flex' : 'none')};
  z-index: ${({ theme }) => theme.zIndexes.minimalContent};

  justify-content: center;
  margin-top: 0;
  margin-bottom: 1rem;

  @media ${mq(sizes.desktop)} {
    display: flex;
    justify-content: center;
    margin-top: 0;
  }
  @media print {
    display: none;
  }
`

export const Title = styled.h1<{
  hasBottomContent: boolean
  hasBackLink?: boolean
}>`
  ${({ theme }) => theme.textStyles.titleH1_26}
  z-index: ${({ theme }) => theme.zIndexes.minimalContent};
  margin: 0;
  margin-top: auto;
  margin-bottom: 2.7rem;
  padding-top: 0;
  text-align: center;
  min-height: ${({ hasBottomContent }) => (hasBottomContent ? '0' : '14.5rem')};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  @media ${mq(sizes.tablet)} {
    min-height: 0;
    display: block;
    margin-top: ${({ hasBottomContent, hasBackLink }) =>
      hasBottomContent || hasBackLink ? '2rem' : '7rem'};
    margin-bottom: ${({ hasBottomContent }) =>
      hasBottomContent ? '3rem' : '0'};
  }
  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.titleH1_36}
    margin-top: 1rem;
    margin-bottom: ${({ hasBottomContent }) =>
      hasBottomContent ? '1rem' : '0'};
    padding-bottom: 2rem;
  }
  br {
    @media ${mq(sizes.tablet)} {
      display: none;
    }
  }
  @media print {
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
  }
`

export const BottomContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 2rem;

  @media ${mq(sizes.tablet)} {
    margin-bottom: auto;
  }

  @media print {
    display: none;
  }
`

export const BackgroundTexures = styled.div`
  @media print {
    display: none;
  }
`

export const Dust = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  opacity: 0.4;

  background-image: url('${publicPathPrefix}/images/Textures/grudge.png');
  background-repeat: repeat;

  @media ${mq(sizes.tablet)} {
    opacity: 0.6;
  }
`

export const IconPattern = styled(Icon)`
  display: flex;
  position: relative;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(0, 0, 0, 0.2);
`

export const IconsPatternDesktop = styled.div`
  display: none;
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;

  @media ${mq(sizes.tablet)} {
    display: flex;
  }

  ${IconPattern} {
    position: relative;
    width: 100%;
    height: 100%;

    & > svg {
      position: relative;
      width: 100%;
      height: 100%;

      & > * {
        position: relative;
        width: 100%;
        height: 100%;
      }
    }
  }
`
export const IconsPatternMobile = styled.div`
  display: flex;
  position: absolute;
  top: -6rem;
  width: 100%;
  height: 100%;
  justify-content: center;

  ${IconPattern} {
    height: 120%;
  }

  @media ${mq(sizes.tablet)} {
    display: none;
  }
`

export const IconsPattern = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
`

const CommonBackgroundSTyles = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndexes.background};

  & .low-opacity {
    opacity: 0.2;
  }
`
export const BackgroundMobile = styled.div`
  ${CommonBackgroundSTyles}
  & img {
    object-position: top center;
  }
  @media ${mq(sizes.tablet)} {
    display: none;
  }
`
export const BackgroundDesktop = styled.div`
  display: none;
  @media ${mq(sizes.tablet)} {
    ${CommonBackgroundSTyles};
    display: block;
  }
`

export const TitleImage = styled.div`
  display: flex;
  align-self: center;
  position: relative;
  width: 23rem;
  height: 10rem;
  margin: 2rem 0;
  z-index: 1;
`

export const LeftImageWrapper = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  @media ${mq(sizes.tablet)} {
    display: flex;
    width: 40%;
    height: 75%;
  }
  @media ${mq(sizes.desktop)} {
    width: 36%;
    height: 69%;
  }
  @media ${mq(sizes.boxContent)} {
    width: 40%;
    height: 80%;
  }
  @media ${mq(sizes.boxBigger)} {
    width: 30%;
    height: 90%;
  }
  & > div {
    position: relative;
    height: 100%;
    width: 100%;
  }
`

export const RightImageWrapper = styled.div<{ $isDoodle?: boolean }>`
  display: none;
  position: absolute;
  ${({ $isDoodle }) =>
    $isDoodle
      ? css`
          @media ${mq(sizes.tablet)} {
            display: flex;
            bottom: 0;
            right: 0;
            width: 30%;
            height: 90%;
          }
          @media ${mq(sizes.desktop)} {
            width: 30%;
            height: 60%;
          }
          @media ${mq(sizes.boxContent)} {
            width: 31%;
            height: 79%;
          }
          @media ${mq(sizes.boxBigger)} {
            width: 25%;
            height: 85%;
          }
        `
      : css`
          @media ${mq(sizes.tablet)} {
            display: flex;
            top: -15%;
            width: 20%;
            height: 107%;
          }
          @media ${mq(sizes.desktop)} {
            top: -2%;
            width: 20%;
            height: 100%;
          }
          @media ${mq(sizes.boxBigger)} {
            width: 13%;
            height: 100%;
          }
        `}
  right: 2%;

  & > div {
    position: relative;
    height: 100%;
    width: 100%;
  }
`

export const MobileImageWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 95%;
  height: 55%;
  @media ${mq(sizes.tablet)} {
    display: none;
  }

  & > div {
    position: relative;
    height: 100%;
    width: 100%;
  }
`

export const SettingsButtonWrapper = styled.div`
  position: absolute;
  top: 4rem;
  right: 1rem;
  z-index: 10;

  @media ${mq(sizes.desktop)} {
    // display: none;
    top: 7rem;
    right: 3rem;
  }
`

export const SettingsButton = styled(Icon)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  border: 1px solid ${({ theme }) => theme.colors.greyCE};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.pureWhite};
  cursor: pointer;
  & svg {
    width: 1.75rem;
    height: 1.75rem;
    color: ${({ theme }) => theme.colors.pureBlack};
  }
`
